import React from 'react';
import Card from './Card';
import ToggleSwitch from './ToggleSwitch';
import EditPencilIcon from '../icons/EditPencilIcon';
import TrashIcon from '../icons/TrashIcon';
import { getCharacterShopOutfit } from '../../utils/characterPresets';
import { getRoomSurfaceImage } from '../../types/myHome';

export interface StoreItem {
  id: string;
  name: string;
  description: string;
  price: number;
  itemCategory: 'usage' | 'qr' | 'seat' | 'seatReservation' | 'seatTakeover' | 'collectible' | 'gacha' | 'cosmetic' | 'furniture' | 'roomSurface' | 'petConsumable' | 'petPlayTicket';
  characterOutfitId?: string;
  roomSurfaceKey?: string;
  isActive?: boolean;
  stock: number;
  isUnlimitedStock: boolean;
  isLimited: boolean;
  salePeriod?: {
    start: Date | string;
    end: Date | string;
  };
  imageUrl?: string;
  // 추가 속성들 (선택적)
  targetType: 'class' | 'subject';
  itemType?: 'point' | 'stamp';
  createdAt?: Date;
  updatedAt?: Date | any; // FieldValue를 포함하기 위해 any 허용
  teacherId?: string;
  subjectId?: string;
  qrData?: string;
  qrCodeExpiry?: number;
  // StoreManagement.tsx와의 호환성을 위한 추가 필드들
  classId?: string;
  subject?: string;
}

interface StoreItemCardForTeacherProps {
  item: StoreItem;
  isMobile?: boolean;
  onEdit?: (item: StoreItem) => void;
  onDelete?: (itemId: string) => void;
  onToggleActive?: (itemId: string, currentStatus: boolean) => void;
  formatDate?: (date: Date) => string;
}

const StoreItemCardForTeacher: React.FC<StoreItemCardForTeacherProps> = ({
  item,
  isMobile = false,
  onEdit,
  onDelete,
  onToggleActive,
  formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  }
}) => {
  const outfit = item.itemCategory === 'cosmetic' ? getCharacterShopOutfit(item.characterOutfitId) : undefined;
  const displayImage = outfit?.shopImageUrl || (item.itemCategory === 'roomSurface' ? getRoomSurfaceImage(item.roomSurfaceKey) : undefined) || item.imageUrl;
  // 교사용 카드는 데스크톱만 지원
  return (
    <Card
      width="243.2px"
      height="434px"
      padding="182px 20px 20px 20px"
      borderRadius="12px"
      boxShadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
      style={{
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* 아이템 이미지 영역 */}
      <div style={{
        width: '243.2px',
        height: '158px',
        left: 0,
        top: 0,
        position: 'absolute',
        background: 'var(--Color-background-sub, #F8F8F8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {displayImage ? (
          <img
            src={displayImage}
            alt={item.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: item.itemCategory === 'cosmetic' ? 'contain' : 'cover'
            }}
          />
        ) : (
          <span style={{
            fontSize: '48px',
            color: '#989898'
          }}>
            📦
          </span>
        )}
      </div>

      {/* 아이템 정보 영역 */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '36px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '139px',
          gap: '16px'
        }}>
          {/* 제목 및 설명 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            height: '75px'
          }}>
            <div style={{
              height: '27px',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '18px',
              fontFamily: 'pretendard',
              fontWeight: '600',
              lineHeight: '27px',
              wordWrap: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {item.name}
            </div>
            <div style={{
              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
              fontSize: '13px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: '19.5px',
              wordWrap: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {item.description || '설명이 없습니다...'}
            </div>
          </div>

          {/* 기간 및 유형 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {/* 기간 - 항상 표시 */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <div style={{
                width: '51px',
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '13px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '19.5px'
              }}>
                기간
              </div>
              <div style={{
                flex: '1',
                textAlign: 'left',
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '13px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '19.5px'
              }}>
                {item.salePeriod 
                  ? `${formatDate(item.salePeriod.start instanceof Date ? item.salePeriod.start : new Date(item.salePeriod.start))}~${formatDate(item.salePeriod.end instanceof Date ? item.salePeriod.end : new Date(item.salePeriod.end))}`
                  : '제한 없음'
                }
              </div>
            </div>
            
            {/* 유형 - 항상 표시 */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <div style={{
                width: '51px',
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '13px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '19.5px'
              }}>
                유형
              </div>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '13px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '19.5px'
              }}>
                {item.itemCategory === 'qr' ? 'QR 코드' :
                 item.itemCategory === 'seat' || item.itemCategory === 'seatReservation' ? '좌석 예약권' :
                 item.itemCategory === 'seatTakeover' ? '좌석 강탈권' :
                 item.itemCategory === 'collectible' ? '소장용' :
                 item.itemCategory === 'cosmetic' ? (item.characterOutfitId === 'orange-star-jacket' ? 'SD 상의' : '캐릭터 복장') :
                 item.itemCategory === 'furniture' ? '가구' :
                 item.itemCategory === 'roomSurface' ? '벽지·바닥' :
                 item.itemCategory === 'petConsumable' ? '펫 소모품' :
                 item.itemCategory === 'petPlayTicket' ? '펫 놀이권' : '사용권'}
              </div>
            </div>
          </div>
        </div>

        {/* 하단 금액/재고/액션 영역 */}
        <div style={{
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-end',
          gap: '41px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '25px'
          }}>
            {/* 금액 */}
            <div style={{
              width: '46px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '12px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '18px'
              }}>
                금액
              </div>
              <div style={{
                height: '31px',
                color: 'var(--Color-text-brand-color, #FF6F0F)',
                fontSize: '24px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                lineHeight: '36px'
              }}>
                {item.price}
              </div>
            </div>

            {/* 재고 */}
            <div style={{
              width: '42px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '12px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '18px'
              }}>
                재고
              </div>
              <div style={{
                height: '31px',
                color: 'var(--Color-text-brand-color, #FF6F0F)',
                fontSize: '24px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                lineHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {item.isUnlimitedStock ? '-' : (item.stock === 0 ? '없음' : item.stock)}
              </div>
            </div>
          </div>

          {/* 토글 스위치 */}
          {onToggleActive && (
            <ToggleSwitch
              checked={item.isActive || false}
              onChange={() => onToggleActive(item.id, item.isActive || false)}
              size="medium"
            />
          )}
        </div>
      </div>

      {/* 상단 태그 및 아이콘 영역 */}
      <div style={{
        width: 'calc(100% - 40px)',
        left: '20px',
        top: '16px',
        position: 'absolute',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* 일회성 태그 */}
        <div>
          {item.isLimited && (
            <div style={{
              padding: '2px 12px',
              background: '#FFD2B5',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <div style={{
                color: 'var(--Color-text-brand-color, #FF6F0F)',
                fontSize: '12px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '18px'
              }}>
                1회 구매
              </div>
            </div>
          )}
        </div>

        {/* 수정/삭제 아이콘 */}
        <div style={{
          height: '22px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {onEdit && (
            <div 
              style={{
                width: '20px',
                height: '20px',
                position: 'relative',
                cursor: 'pointer'
              }}
              onClick={() => onEdit(item)}
            >
              <div style={{
                width: '16px',
                height: '16px',
                left: '2px',
                top: '2px',
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <EditPencilIcon width={12} height={12} color="#989898" />
              </div>
            </div>
          )}
          {onDelete && (
            <div 
              style={{
                width: '20px',
                height: '20px',
                position: 'relative',
                cursor: 'pointer'
              }}
              onClick={() => {
                if (window.confirm('이 아이템을 삭제하시겠습니까?')) {
                  onDelete(item.id);
                }
              }}
            >
              <div style={{
                width: '16px',
                height: '16px',
                left: '2px',
                top: '2px',
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <TrashIcon width={11.47} height={12.8} color="#989898" />
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default StoreItemCardForTeacher;
